// Backlog 086 — service `type:` ↔ folder content alignment.
//
// Decision 020 §3 ("every assumption is visible") demands that the platform
// fail loudly when a service's declared `type:` doesn't match what's in the
// folder. Before this check, a yaml that said `type: nextjs` against a folder
// containing only static HTML would pass validate, fail in Cloud Build five
// minutes later with `Cannot find module 'next/script'`, and leave the dev
// guessing what went wrong.
//
// The predicates are filesystem-agnostic so the same code runs in two layers:
//
//   * CLI preflight — `services/<svc>/` on disk, before the tarball is built.
//   * Server-side  — the gzipped tarball that arrived on `POST /plug`.
//
// Each layer constructs its own `ServiceFolderReader` (over fs vs over the
// in-memory tarball entries) and the predicate logic stays pure.

import type { ServiceConfig } from './types.js';

/**
 * Filesystem-agnostic view of a service folder. Implementations cover the CLI
 * (real fs) and the API (gzipped tarball). Only top-level files are
 * consulted — none of the spec table's checks recurse into subdirectories.
 *
 * `readText` returns null for missing files. The reader is responsible for
 * size-cap protection — a 50MB `package.json` is never legitimate, and the
 * predicate doesn't pre-flight that. CLI/API readers cap at a reasonable
 * size and surface null when the file is too big to inspect.
 */
export interface ServiceFolderReader {
  has(path: string): boolean;
  readText(path: string): string | null;
}

export type AlignmentResult =
  | { ok: true }
  | { ok: false; message: string };

/**
 * Backlog 076 — HTTP-mode cron services have no source folder. The platform
 * runs them from `curlimages/curl` against an external URL; there is nothing
 * to align against. Returns true when this service should be skipped entirely.
 *
 * Promoted from `packages/cli/src/services/cron-mode.ts` so the API can reuse
 * it without crossing the shared/cli boundary backwards. The CLI re-export
 * remains for backwards compatibility with existing imports (init/scaffold/dev).
 */
export function isHttpModeCronService(svc: {
  type?: string;
  run?: unknown;
}): boolean {
  if (svc.type !== 'cron') return false;
  if (typeof svc.run !== 'string') return false;
  return svc.run.startsWith('http://') || svc.run.startsWith('https://');
}

/**
 * Check that the declared `type:` aligns with the contents of the service
 * folder. Returns `{ ok: true }` on success, or `{ ok: false, message }`
 * with a §23-voiced multi-line remediation block on failure. The error
 * message text is stable — both CLI preflight and server-side validate
 * surface it verbatim.
 */
export function checkServiceContentAlignment(
  serviceName: string,
  service: ServiceConfig,
  reader: ServiceFolderReader,
): AlignmentResult {
  // HTTP-mode cron — no folder, no check (backlog 076). Job-mode cron falls
  // through to the node check below; the platform synthesises a cron wrapper
  // around a node script.
  if (service.type === 'cron' && isHttpModeCronService(service)) {
    return { ok: true };
  }

  switch (service.type) {
    case 'node':
    case 'cron':
      return checkNode(serviceName, service, reader);
    case 'nextjs':
      return checkNextjs(serviceName, reader);
    case 'python':
      return checkPython(serviceName, reader);
    case 'static':
      return checkStatic(serviceName, service, reader);
    default:
      // `validateConfig` already rejected unknown types — this branch is
      // defensive only.
      return { ok: true };
  }
}

// ── node + cron-job-mode ────────────────────────────────────────────────────

/**
 * Root entry files the Node Dockerfile synthesis (resolveNodeEntryCmd in
 * dockerfile-render.ts) falls back to when package.json declares neither
 * `main` nor `scripts.start`. Kept in the same priority order so the
 * validator accepts exactly what the renderer can run.
 */
const NODE_ROOT_ENTRY_FILES = ['server.js', 'app.js', 'index.js'] as const;

function checkNode(
  serviceName: string,
  service: ServiceConfig,
  reader: ServiceFolderReader,
): AlignmentResult {
  if (!reader.has('package.json')) {
    return {
      ok: false,
      message: nodeNoPackageJsonError(serviceName, service.type),
    };
  }
  const pkgRaw = reader.readText('package.json');
  if (pkgRaw === null) {
    // Reader has it but couldn't read it (size cap). Treat as a soft pass —
    // the build step will fail more clearly. We don't want to block on a
    // reader edge case.
    return { ok: true };
  }
  let pkg: any;
  try {
    pkg = JSON.parse(pkgRaw);
  } catch {
    return {
      ok: false,
      message: malformedPackageJsonError(serviceName, service.type),
    };
  }
  const hasMain = typeof pkg?.main === 'string' && pkg.main.length > 0;
  const hasStart =
    pkg?.scripts && typeof pkg.scripts.start === 'string' && pkg.scripts.start.length > 0;
  // Symmetry with resolveNodeEntryCmd (dockerfile-render.ts): when neither
  // `main` nor `scripts.start` is declared, the synthesized Dockerfile falls
  // back to scanning for a root entry file (server.js → app.js → index.js).
  // The validator must accept the same set, or it would gate users whose app
  // would in fact deploy and run fine.
  const hasRootEntry = NODE_ROOT_ENTRY_FILES.some((f) => reader.has(f));
  if (!hasMain && !hasStart && !hasRootEntry) {
    return {
      ok: false,
      message: noEntrypointError(serviceName, service.type),
    };
  }
  // Decision 019 — typescript on node requires tsconfig.json so the build
  // step's `tsc` invocation has something to chew on.
  if (service.lang === 'typescript' && !reader.has('tsconfig.json')) {
    return {
      ok: false,
      message: missingTsconfigError(serviceName),
    };
  }
  return { ok: true };
}

function nodeNoPackageJsonError(serviceName: string, type: string): string {
  const article = type === 'cron' ? 'cron job' : 'Node.js service';
  return [
    `Validation failed: service '${serviceName}' declares type: ${type} but the folder`,
    `has no package.json.`,
    '',
    '  Pick one:',
    `    1. Create services/${serviceName}/package.json (typical for a ${article}).`,
    "    2. Change type to 'static' if the folder contains pre-built HTML.",
    "    3. Change type to 'python' if the folder contains a Python project.",
  ].join('\n');
}

function malformedPackageJsonError(serviceName: string, type: string): string {
  return [
    `Validation failed: service '${serviceName}' declares type: ${type} but its`,
    `package.json is not valid JSON.`,
    '',
    '  Pick one:',
    `    1. Fix services/${serviceName}/package.json so it parses.`,
    "    2. Change type to 'static' if the folder contains pre-built HTML.",
  ].join('\n');
}

function noEntrypointError(serviceName: string, type: string): string {
  return [
    `Validation failed: service '${serviceName}' declares type: ${type} but its`,
    `package.json has neither a "main" entry nor a "scripts.start".`,
    '',
    '  Pick one:',
    `    1. Add "main": "index.js" (or your entrypoint) to services/${serviceName}/package.json.`,
    `    2. Add "scripts": { "start": "node server.js" } to the same file.`,
    "    3. Change type to 'static' if this isn't a runnable Node service.",
  ].join('\n');
}

function missingTsconfigError(serviceName: string): string {
  return [
    `Validation failed: service '${serviceName}' declares lang: typescript but the`,
    `folder has no tsconfig.json (decision 019).`,
    '',
    '  Pick one:',
    `    1. Create services/${serviceName}/tsconfig.json (the build step's tsc reads it).`,
    "    2. Change lang to 'javascript' if you don't intend to compile TypeScript.",
  ].join('\n');
}

// ── nextjs ──────────────────────────────────────────────────────────────────

function checkNextjs(
  serviceName: string,
  reader: ServiceFolderReader,
): AlignmentResult {
  if (!reader.has('package.json')) {
    return {
      ok: false,
      message: [
        `Validation failed: service '${serviceName}' declares type: nextjs but the folder`,
        `has no package.json.`,
        '',
        '  Pick one:',
        `    1. Create services/${serviceName}/package.json with "next" as a dependency.`,
        "    2. Change type to 'static' if the folder contains pre-built HTML.",
      ].join('\n'),
    };
  }
  // Plug-blockers 2026-06-12 (smoke Finding 6): `next.config.*` is NOT
  // required. Modern `create-next-app` scaffolds with `next.config.mjs`/`.ts`
  // or omit a config entirely, and a hand-rolled minimal Next.js app needs
  // none. The platform's Dockerfile serves via `next start`, which reads the
  // config when present and runs fine without it. A `package.json` carrying
  // `next` is the only real signal, and validating its presence here is out of
  // scope — the build step surfaces a missing `next` dependency clearly. So
  // any config form (`.js`/`.mjs`/`.ts`) or none is accepted silently.
  return { ok: true };
}

// ── python ──────────────────────────────────────────────────────────────────

function checkPython(
  serviceName: string,
  reader: ServiceFolderReader,
): AlignmentResult {
  // Pip path: requirements.txt must exist AND be non-empty (an empty file is
  // a placeholder, not a working pip install — the build step would no-op).
  const reqRaw = reader.readText('requirements.txt');
  if (reader.has('requirements.txt') && reqRaw !== null && reqRaw.trim().length > 0) {
    return { ok: true };
  }

  // Poetry / PEP 621 path: pyproject.toml must exist AND declare a recognised
  // section. Substring match is intentional — full TOML parsing is overkill
  // for "is this a real Python project file or an empty placeholder".
  const pyprojRaw = reader.readText('pyproject.toml');
  if (reader.has('pyproject.toml') && pyprojRaw !== null) {
    if (pyprojRaw.includes('[tool.poetry]') || pyprojRaw.includes('[project]')) {
      return { ok: true };
    }
    return {
      ok: false,
      message: [
        `Validation failed: service '${serviceName}' declares type: python but its`,
        `pyproject.toml has neither [tool.poetry] (poetry) nor [project] (PEP 621).`,
        '',
        '  Pick one:',
        `    1. Add a [tool.poetry] section to services/${serviceName}/pyproject.toml.`,
        '    2. Add a [project] section (PEP 621) to the same file.',
        `    3. Use services/${serviceName}/requirements.txt instead.`,
      ].join('\n'),
    };
  }

  return {
    ok: false,
    message: [
      `Validation failed: service '${serviceName}' declares type: python but the folder`,
      `has neither requirements.txt nor pyproject.toml.`,
      '',
      '  Pick one:',
      `    1. Create services/${serviceName}/requirements.txt and list your pip dependencies.`,
      `    2. Create services/${serviceName}/pyproject.toml with [tool.poetry] or [project].`,
      "    3. Change type to 'node' if this is a Node.js service.",
    ].join('\n'),
  };
}

// ── static ──────────────────────────────────────────────────────────────────

/**
 * Backlog 108 default for `services.<svc>.build.output`. The platform looks
 * for the built `index.html` here when `build:` is set but `output:` is
 * omitted. Kept as a constant so the validator and the deployer agree on
 * the default in one place.
 */
export const DEFAULT_STATIC_BUILD_OUTPUT = 'dist';

function checkStatic(
  serviceName: string,
  service: ServiceConfig,
  reader: ServiceFolderReader,
): AlignmentResult {
  // Backlog 108 — `build:` shifts the index.html expectation from the service
  // root to the declared `output/` directory (default `dist/`). Source trees
  // for Astro / Vite / SvelteKit don't have an index.html at the root —
  // they produce one in the build output folder. The build itself runs
  // server-side at deploy time; this preflight only checks that the
  // declared output path is the right place to look.
  if (service.build) {
    const outputDir = service.build.output ?? DEFAULT_STATIC_BUILD_OUTPUT;
    const expectedPath = `${outputDir}/index.html`;
    if (!reader.has(expectedPath)) {
      return {
        ok: false,
        message: [
          `Validation failed: service '${serviceName}' declares type: static with a`,
          `build step but the folder has no ${expectedPath} (built output).`,
          '',
          '  Pick one:',
          `    1. Run the build locally so services/${serviceName}/${outputDir}/index.html exists.`,
          `    2. Adjust 'build.output' to match where your build writes index.html.`,
          "    3. Remove the 'build:' block if this folder already contains pre-built HTML.",
        ].join('\n'),
      };
    }
    return { ok: true };
  }

  if (!reader.has('index.html')) {
    return {
      ok: false,
      message: [
        `Validation failed: service '${serviceName}' declares type: static but the folder`,
        `has no index.html at the root.`,
        '',
        '  Pick one:',
        `    1. Create services/${serviceName}/index.html (or move it to the folder root).`,
        "    2. Change type to 'nextjs' if this is a Next.js app.",
        "    3. Change type to 'node' if you're running a Node.js server.",
        "    4. Add a 'build:' block if this is an Astro/Vite/SvelteKit source tree.",
      ].join('\n'),
    };
  }
  return { ok: true };
}
